import { useFlags } from "launchdarkly-react-client-sdk";

import { useAppSelector } from "app/hooks";
import {
  AFTER_VISIT_SUMMARY_FEATURE,
  PAYWALL_BYPASS
} from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { selectFeatures } from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetFeatureSubscriptionsQuery } from "services/paywall";

/**
 * Whether the After Visit Summary surfaces render at all.
 *
 * Two gates, and no role gate apart from Suno admins (below): the letter is generated from the
 * scribe recording, so the PRACTICE needs the Ambient Scribe license — but reviewing and emailing it
 * is front-desk work, so every staff member who can open the page gets the button (the product
 * decision behind the feature; see the plan for Intercom 215475744511616).
 *
 * The license gate is the practice's Ambient Scribe subscription (`/paywall/feature-subscriptions/`,
 * same source as `useAmbientScribeTrialStatus`), NOT the per-user seat check behind
 * `/auth/users/me/features/`. The seat check hid the button from exactly the people the spec says
 * must see it: front-desk staff without a scribe seat, and Suno-role users in a switched practice.
 * It also matches the backend's auto-generation gate (`PaywallService.can_use_feature`, practice
 * level), so the button and the automatic letters can never disagree about who is licensed.
 *
 * One exception (SN-19868): on a trial without a card each person who can hold a seat needs their
 * own live one, and the backend refuses them otherwise. `/me/features/` says so with
 * `seat_required` (read at login into `auth.features`), and that hides the button from them. Front
 * desk cannot hold a seat, so it is never `seat_required` and keeps the letter.
 *
 * The one role exception (SN-20150): Suno admins get the letter in every practice, subscribed or
 * not, because the backend lets them generate it anywhere (`can_use_feature` allows the Suno group
 * for Ambient Scribe). Every other Scribe gate reads `/me/features/`, which the backend already
 * answers for them, so do not copy this bypass into those.
 *
 * A still-resolving LD flag reads as "off": these are additive surfaces, and rendering-then-hiding
 * is worse than appearing a beat later.
 */
export default function useAfterVisitSummaryAccess(): {
  /**
   * Flag on AND either this user is a Suno admin, or the practice holds an active/trialing Ambient
   * Scribe subscription AND this user is not refused for lack of their own seat on a trial without
   * a card.
   */
  isEnabled: boolean;
  isFlagOn: boolean;
} {
  const flags = useFlags();
  const isFlagOn = flags[AFTER_VISIT_SUMMARY_FEATURE] === true;
  // Same default as `useCheckPaywallFeatureAvailability`: a missing bypass flag means bypass.
  const paywallBypassEnabled = flags[PAYWALL_BYPASS] ?? true;
  const features = useAppSelector(selectFeatures);
  const { isSunoAdmin } = useUserRoles();

  // The subscriptions list is a request; only pay for it once the flag says the feature exists.
  // An explicit `limit` matters: the endpoint's `CustomLimitOffsetPagination` defaults to 5 rows
  // ordered newest-first, so a practice with older scribe subscriptions among newer feature rows
  // would silently read as unlicensed under the default.
  const { data: featureSubscriptions } = useGetFeatureSubscriptionsQuery(
    { limit: 100 },
    { skip: !isFlagOn || paywallBypassEnabled || isSunoAdmin }
  );

  const isPracticeLicensed = Boolean(
    featureSubscriptions?.some(
      (subscription) =>
        subscription.feature?.feature_key === AMBIENT_SCRIBE_FEATURE_KEY &&
        subscription.is_active
    )
  );
  const isLicenseAvailable =
    paywallBypassEnabled ||
    isSunoAdmin ||
    (isPracticeLicensed &&
      !features?.[AMBIENT_SCRIBE_FEATURE_KEY]?.seat_required);

  return { isEnabled: isFlagOn && isLicenseAvailable, isFlagOn };
}
